import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { SiteService } from '@card-software/library';
import { TranslateService } from '@ngx-translate/core';
import { Observable, finalize, forkJoin, map, of, switchMap } from 'rxjs';
import _ from 'lodash';
import { CostCenter } from '../../models/cost-center/cost-center';
import { ExpenseConcept } from '../../models/expense-concept/expense-concept';
import { InventoryType } from '../../models/inventory-type/inventory-type';
import {
  GlAccountRule,
  GlAccountRuleResolution,
  GlAccountRuleResolveLine,
  GlAccountRuleRole,
  GlAccountRuleScope,
} from '../../models/gl-account-rule/gl-account-rule';
import { GlAccountRuleRoles } from '../../models/roles/roles-group';
import { CostCenterProxyService } from '../../proxies/cost-center.proxy.service';
import { ExpenseConceptProxyService } from '../../proxies/expense-concept.proxy.service';
import { GlAccountRuleProxyService } from '../../proxies/gl-account-rule.proxy.service';
import { InventoryTypeProxyService } from '../../proxies/inventory-type.proxy.service';
import { LocaleHelperService } from '../../services/locale-helper.service';
import { SiteCompanyService } from '../../services/site-company.service';
import { UserAuthenticationService } from '../../services/user-authentication.service';
import { GlAccountRuleDrawerRequest } from './gl-account-rule-drawer/gl-account-rule-drawer.component';

type RuleView = 'costCenter' | 'concept' | 'inventoryType';

/** One entry in the left-hand list, whatever the view. */
interface SideItem {
  /** Cost center code, concept code or inventory type id; '' = the general (any cost center) entry. */
  value: string;
  title: string;
  subtitle: string;
  depth: number;
  ruleCount: number;
  incomplete: boolean;
}

interface CostCenterRuleRow {
  rule: GlAccountRule;
  scopeLabel: string;
  dimensionLabel: string;
  roleLabel: string;
  origin: string;
  isOwn: boolean;
  /** Null while the resolver hasn't answered. */
  applies: boolean | null;
  overriddenBy: string | null;
}

interface LadderRow {
  rule: GlAccountRule;
  depth: number;
  step: string;
  target: string;
  roleLabel: string;
}

interface CoverageRow {
  code: string;
  name: string;
  depth: number;
  account: string;
  accountName: string;
  via: string;
  resolved: boolean;
}

interface InventoryCell {
  role: GlAccountRuleRole;
  rule: GlAccountRule | null;
}

interface InventoryRow {
  costCenterCode: string | null;
  label: string;
  cells: InventoryCell[];
}

/** The inventory grid's columns, in posting order: receipt debit, receipt credit, consumption, tax. */
const INVENTORY_ROLES: GlAccountRuleRole[] = [
  GlAccountRuleRole.Inventory,
  GlAccountRuleRole.Offset,
  GlAccountRuleRole.Expense,
  GlAccountRuleRole.Tax,
];

/** Search page size large enough for every active rule of one company. */
const ALL_RULES_PAGE_SIZE = 5000;

/**
 * GL account rules (#5784) — which account each line takes by cost center, expense concept or inventory
 * type. Three views over the same rules; which rule wins always comes from the API (resolve for saved
 * rules, preview for a draft) so this page never re-implements the ranking that posting uses.
 */
@Component({
  selector: 'app-gl-account-rules',
  templateUrl: './gl-account-rules.component.html',
  styleUrl: './gl-account-rules.component.scss',
})
export class GlAccountRulesComponent implements OnInit {
  private readonly ruleProxy = inject(GlAccountRuleProxyService);
  private readonly costCenterProxy = inject(CostCenterProxyService);
  private readonly conceptProxy = inject(ExpenseConceptProxyService);
  private readonly inventoryTypeProxy = inject(InventoryTypeProxyService);
  private readonly siteCompany = inject(SiteCompanyService);
  private readonly locale = inject(LocaleHelperService);
  private readonly translate = inject(TranslateService);
  private readonly userAuth = inject(UserAuthenticationService);

  readonly Scope = GlAccountRuleScope;
  readonly Role = GlAccountRuleRole;

  /** Viewers read and preview; the API refuses create/update without the admin role as well. */
  readonly canEdit = this.userAuth.roles.includes(GlAccountRuleRoles.GlAccountRuleAdmin);

  readonly isLoading = signal(0);
  readonly companyId = signal<string | null>(null);
  readonly companyName = signal('');
  readonly costCenters = signal<CostCenter[]>([]);
  readonly concepts = signal<ExpenseConcept[]>([]);
  readonly inventoryTypes = signal<InventoryType[]>([]);
  readonly rules = signal<GlAccountRule[]>([]);

  readonly view = signal<RuleView>('costCenter');
  readonly search = signal('');
  readonly selectedCostCenter = signal('');
  readonly selectedConcept = signal('');
  readonly selectedInventoryType = signal('');

  /** Resolver answers for the current selection, keyed by the line key sent. */
  readonly resolutions = signal<Map<string, GlAccountRuleResolution>>(new Map());

  readonly drawer = signal<GlAccountRuleDrawerRequest | null>(null);

  readonly viewOptions = computed(() => [
    { label: this.t('GlAccountRule_View_CostCenter', 'By cost center'), value: 'costCenter' as RuleView },
    { label: this.t('GlAccountRule_View_Concept', 'By concept'), value: 'concept' as RuleView },
    { label: this.t('GlAccountRule_View_InventoryType', 'By inventory type'), value: 'inventoryType' as RuleView },
  ]);

  private readonly costCenterByCode = computed(() => new Map(this.costCenters().map((c) => [c.code, c])));
  private readonly activeConcepts = computed(() => this.concepts().filter((c) => c.isActive));
  private readonly activeCostCenters = computed(() => this.costCenters().filter((c) => c.isActive !== false));

  readonly sideTitle = computed(() => {
    switch (this.view()) {
      case 'costCenter':
        return this.t('GlAccountRule_Side_CostCenters', 'Cost centers');
      case 'concept':
        return this.t('GlAccountRule_Side_Concepts', 'Concepts');
      default:
        return this.t('GlAccountRule_Side_InventoryTypes', 'Inventory types');
    }
  });

  readonly sideItems = computed<SideItem[]>(() => {
    const term = this.search().trim().toLowerCase();
    const matches = (item: SideItem) => !term || item.title.toLowerCase().includes(term) || item.subtitle.toLowerCase().includes(term);
    const rules = this.rules();

    switch (this.view()) {
      case 'costCenter': {
        const general: SideItem = {
          value: '',
          title: this.t('GlAccountRule_AnyCostCenter_General', 'Any cost center (general)'),
          subtitle: this.t('GlAccountRule_AppliesToAll', 'Applies to all'),
          depth: 0,
          ruleCount: rules.filter((r) => _.isNil(r.costCenterCode)).length,
          incomplete: false,
        };
        const centers = _.sortBy(this.costCenters(), (c) => c.code).map<SideItem>((c) => ({
          value: c.code,
          title: this.costCenterName(c.code),
          subtitle: c.code,
          depth: this.chain(c.code).length,
          ruleCount: rules.filter((r) => r.costCenterCode === c.code).length,
          incomplete: false,
        }));
        return [general, ...centers].filter(matches);
      }
      case 'concept':
        return this.activeConcepts()
          .map<SideItem>((c) => {
            const count = rules.filter((r) => r.scope === GlAccountRuleScope.ExpenseClaim && r.conceptCode === c.code).length;
            return {
              value: c.code,
              title: this.conceptName(c.code),
              subtitle: c.code,
              depth: 0,
              ruleCount: count,
              incomplete: count === 0,
            };
          })
          .filter(matches);
      default:
        return this.inventoryTypes()
          .map<SideItem>((type) => {
            const typeRules = rules.filter((r) => r.scope === GlAccountRuleScope.InventoryMovement && r.inventoryTypeReference === type.id);
            const configured = [GlAccountRuleRole.Inventory, GlAccountRuleRole.Offset, GlAccountRuleRole.Expense].filter((role) =>
              typeRules.some((r) => r.role === role),
            ).length;
            return {
              value: type.id,
              title: this.inventoryTypeName(type.id),
              subtitle: this.t('GlAccountRule_RolesConfigured', '{n} of 3 roles configured').replace('{n}', String(configured)),
              depth: 0,
              ruleCount: typeRules.length,
              incomplete: this.inventoryWarnings(type.id).length > 0,
            };
          })
          .filter(matches);
    }
  });

  readonly selectedValue = computed(() => {
    switch (this.view()) {
      case 'costCenter':
        return this.selectedCostCenter();
      case 'concept':
        return this.selectedConcept();
      default:
        return this.selectedInventoryType();
    }
  });

  // ---- By cost center ----

  readonly costCenterRows = computed<CostCenterRuleRow[]>(() => {
    const selected = this.selectedCostCenter() || null;
    const chain = _.isNil(selected) ? [] : this.chain(selected);
    const resolutions = this.resolutions();

    const rows = this.rules()
      .filter((r) => _.isNil(r.costCenterCode) || chain.includes(r.costCenterCode))
      .map<CostCenterRuleRow>((rule) => {
        const winner = resolutions.get(rule.id);
        const isOwn = !_.isNil(selected) && rule.costCenterCode === selected;
        return {
          rule,
          scopeLabel: this.scopeLabel(rule.scope),
          dimensionLabel: this.dimensionLabel(rule),
          roleLabel: this.roleLabel(rule.role),
          origin: isOwn
            ? this.t('GlAccountRule_Origin_Own', 'Own')
            : _.isNil(rule.costCenterCode)
              ? this.t('GlAccountRule_Origin_General', 'General')
              : this.t('GlAccountRule_Origin_InheritedFrom', 'Inherited from {name}').replace(
                  '{name}',
                  this.costCenterName(rule.costCenterCode),
                ),
          isOwn,
          applies: _.isNil(winner) ? null : winner.ruleReference === rule.id,
          overriddenBy: _.isNil(winner) || winner.ruleReference === rule.id ? null : winner.ruleReference,
        };
      });

    return _.sortBy(rows, [
      (r) => r.rule.scope,
      (r) => r.dimensionLabel,
      (r) => INVENTORY_ROLES.indexOf(r.rule.role),
      (r) => (_.isNil(r.rule.costCenterCode) ? chain.length : chain.indexOf(r.rule.costCenterCode)),
    ]);
  });

  readonly costCenterHeader = computed(() => {
    const selected = this.selectedCostCenter();
    if (!selected) {
      return {
        name: this.t('GlAccountRule_AnyCostCenter_General', 'Any cost center (general)'),
        code: this.t('GlAccountRule_GeneralRules', 'general rules'),
        path: this.t('GlAccountRule_GeneralPath', 'Top level: applies unless a more specific cost center overrides it'),
      };
    }
    return {
      name: this.costCenterName(selected),
      code: selected,
      path: this.chain(selected)
        .slice()
        .reverse()
        .map((code) => this.costCenterName(code))
        .join(' › '),
    };
  });

  readonly costCenterCounts = computed(() => {
    const rows = this.costCenterRows();
    return {
      own: rows.filter((r) => r.isOwn).length,
      inherited: rows.filter((r) => !r.isOwn && r.applies === true).length,
      overridden: rows.filter((r) => r.applies === false).length,
    };
  });

  /** Lines that would post with no account in the selected cost center. */
  readonly costCenterGaps = computed(() => {
    const resolutions = this.resolutions();
    const gaps: string[] = [];
    const concepts = this.activeConcepts().filter((c) => resolutions.get(this.gapKey('concept', c.code))?.isResolved === false);
    if (concepts.length > 0) {
      gaps.push(
        this.t('GlAccountRule_Gap_Concepts', 'Expense claims with no account for: {list}.').replace(
          '{list}',
          concepts.map((c) => this.conceptName(c.code)).join(', '),
        ),
      );
    }
    const types = this.inventoryTypes().filter((t) => resolutions.get(this.gapKey('inventory', t.id))?.isResolved === false);
    if (types.length > 0) {
      gaps.push(
        this.t('GlAccountRule_Gap_InventoryTypes', 'Consumption with no expense account for: {list}.').replace(
          '{list}',
          types.map((t) => this.inventoryTypeName(t.id)).join(', '),
        ),
      );
    }
    return gaps;
  });

  // ---- By concept ----

  readonly ladderRows = computed<LadderRow[]>(() => {
    const concept = this.selectedConcept();
    const rules = this.rules().filter((r) => r.scope === GlAccountRuleScope.ExpenseClaim && r.conceptCode === concept);
    const depthOf = (code: string | null) => (_.isNil(code) ? 0 : this.chain(code).length);
    return _.sortBy(rules, [(r) => depthOf(r.costCenterCode), (r) => r.role]).map((rule) => ({
      rule,
      depth: depthOf(rule.costCenterCode),
      step: _.isNil(rule.costCenterCode)
        ? this.t('GlAccountRule_Step_General', 'General')
        : this.hasChildren(rule.costCenterCode)
          ? this.t('GlAccountRule_Step_Parent', 'Parent node')
          : this.t('GlAccountRule_Step_CostCenter', 'Cost center'),
      target: _.isNil(rule.costCenterCode)
        ? this.t('GlAccountRule_AnyCostCenter', 'Any cost center')
        : this.costCenterName(rule.costCenterCode),
      roleLabel: this.roleLabel(rule.role),
    }));
  });

  readonly coverageRows = computed<CoverageRow[]>(() => {
    const resolutions = this.resolutions();
    const rulesById = new Map(this.rules().map((r) => [r.id, r]));
    return _.sortBy(this.activeCostCenters(), (c) => c.code).map((center) => {
      const result = resolutions.get(this.coverageKey(center.code));
      const rule = _.isNil(result?.ruleReference) ? undefined : rulesById.get(result.ruleReference);
      let via = this.t('GlAccountRule_Via_None', 'No rule');
      if (!_.isNil(rule)) {
        via =
          rule.costCenterCode === center.code
            ? this.t('GlAccountRule_Via_Own', 'Own rule')
            : _.isNil(rule.costCenterCode)
              ? this.t('GlAccountRule_Origin_General', 'General')
              : this.t('GlAccountRule_Origin_InheritedFrom', 'Inherited from {name}').replace(
                  '{name}',
                  this.costCenterName(rule.costCenterCode),
                );
      }
      return {
        code: center.code,
        name: this.costCenterName(center.code),
        depth: this.chain(center.code).length - 1,
        account: result?.glAccount?.number ?? '—',
        accountName: this.locale.getLocalizedValue(result?.glAccount?.name) ?? '',
        via,
        resolved: result?.isResolved === true,
      };
    });
  });

  // ---- By inventory type ----

  readonly inventoryRoles = INVENTORY_ROLES;

  readonly inventoryRows = computed<InventoryRow[]>(() => {
    const type = this.selectedInventoryType();
    const rules = this.rules().filter((r) => r.scope === GlAccountRuleScope.InventoryMovement && r.inventoryTypeReference === type);
    const costCenters = _.uniq(rules.map((r) => r.costCenterCode).filter((c): c is string => !_.isNil(c))).sort();
    return [null, ...costCenters].map((code) => ({
      costCenterCode: code,
      label: _.isNil(code) ? this.t('GlAccountRule_AnyCostCenter', 'Any cost center') : this.costCenterName(code),
      cells: INVENTORY_ROLES.map((role) => ({
        role,
        rule: rules.find((r) => r.role === role && r.costCenterCode === code) ?? null,
      })),
    }));
  });

  readonly inventoryWarningList = computed(() => this.inventoryWarnings(this.selectedInventoryType()));

  ngOnInit(): void {
    this.loadAll();
  }

  // ---- data ----

  private startLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((v) => v - 1);
  }

  /** Reference data once, then the company's rules; every view filters these in memory. */
  private loadAll(): void {
    this.startLoading();
    this.siteCompany
      .getFinancialCompany(SiteService.getSelectedSiteId())
      .pipe(
        switchMap((company) => {
          this.companyId.set(company?.id ?? null);
          this.companyName.set(company?.companyName ?? '');
          return forkJoin({
            costCenters: this.costCenterProxy.getAll().pipe(map((r) => r.data ?? [])),
            concepts: this.conceptProxy.search({}, 1, ALL_RULES_PAGE_SIZE, 'code', 1).pipe(map((r) => r.data?.items ?? [])),
            inventoryTypes: this.inventoryTypeProxy.getAll().pipe(map((r) => r.data ?? [])),
          });
        }),
        finalize(() => this.stopLoading()),
      )
      .subscribe({
        next: ({ costCenters, concepts, inventoryTypes }) => {
          this.costCenters.set(costCenters);
          this.concepts.set(concepts);
          this.inventoryTypes.set(_.sortBy(inventoryTypes, (t) => this.locale.getLocalizedValue(t.name)));
          this.selectedConcept.set(this.activeConcepts()[0]?.code ?? '');
          this.selectedInventoryType.set(this.inventoryTypes()[0]?.id ?? '');
          this.loadRules();
        },
        error: () => undefined,
      });
  }

  private loadRules(): void {
    const companyId = this.companyId();
    if (_.isNil(companyId)) {
      this.rules.set([]);
      return;
    }
    this.startLoading();
    this.ruleProxy
      .search({ financialCompanyReference: companyId, isActive: true }, 1, ALL_RULES_PAGE_SIZE, 'costCenterCode', 1)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          this.rules.set(response.data?.items ?? []);
          this.refreshResolutions();
        },
        error: () => this.rules.set([]),
      });
  }

  /**
   * One resolve call per scope + role the current view needs — never one per rule or per cost center.
   * The inventory view reads the rules directly and needs none.
   */
  private refreshResolutions(): void {
    const companyId = this.companyId();
    if (_.isNil(companyId) || this.view() === 'inventoryType') {
      this.resolutions.set(new Map());
      return;
    }

    const groups = this.view() === 'costCenter' ? this.costCenterResolveGroups() : this.conceptResolveGroups();
    const calls: Observable<GlAccountRuleResolution[]>[] = groups
      .filter((g) => g.lines.length > 0)
      .map((g) =>
        this.ruleProxy
          .resolve({ scope: g.scope, role: g.role, financialCompanyReference: companyId, lines: g.lines })
          .pipe(map((r) => r.data ?? [])),
      );

    if (calls.length === 0) {
      this.resolutions.set(new Map());
      return;
    }
    this.startLoading();
    forkJoin(calls)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (results) => this.resolutions.set(new Map(results.flat().map((r) => [r.key, r]))),
        error: () => this.resolutions.set(new Map()),
      });
  }

  /** Each listed rule's own line (does it win here?) plus one line per concept / inventory type for the gaps. */
  private costCenterResolveGroups(): { scope: GlAccountRuleScope; role: GlAccountRuleRole; lines: GlAccountRuleResolveLine[] }[] {
    const costCenter = this.selectedCostCenter() || null;
    const chain = _.isNil(costCenter) ? [] : this.chain(costCenter);
    const listed = this.rules().filter((r) => _.isNil(r.costCenterCode) || chain.includes(r.costCenterCode));
    const byKey = _.groupBy(listed, (r) => `${r.scope}|${r.role}`);

    const groups = Object.values(byKey).map((rules) => ({
      scope: rules[0].scope,
      role: rules[0].role,
      lines: rules.map<GlAccountRuleResolveLine>((r) => ({
        key: r.id,
        costCenterCode: costCenter,
        conceptCode: r.conceptCode,
        inventoryTypeReference: r.inventoryTypeReference,
      })),
    }));

    groups.push({
      scope: GlAccountRuleScope.ExpenseClaim,
      role: GlAccountRuleRole.Expense,
      lines: this.activeConcepts().map((c) => ({
        key: this.gapKey('concept', c.code),
        costCenterCode: costCenter,
        conceptCode: c.code,
        inventoryTypeReference: null,
      })),
    });
    groups.push({
      scope: GlAccountRuleScope.InventoryMovement,
      role: GlAccountRuleRole.Expense,
      lines: this.inventoryTypes().map((t) => ({
        key: this.gapKey('inventory', t.id),
        costCenterCode: costCenter,
        conceptCode: null,
        inventoryTypeReference: t.id,
      })),
    });
    return groups;
  }

  /** The expense account every active cost center ends up with for the selected concept. */
  private conceptResolveGroups(): { scope: GlAccountRuleScope; role: GlAccountRuleRole; lines: GlAccountRuleResolveLine[] }[] {
    const concept = this.selectedConcept();
    if (!concept) return [];
    return [
      {
        scope: GlAccountRuleScope.ExpenseClaim,
        role: GlAccountRuleRole.Expense,
        lines: this.activeCostCenters().map((c) => ({
          key: this.coverageKey(c.code),
          costCenterCode: c.code,
          conceptCode: concept,
          inventoryTypeReference: null,
        })),
      },
    ];
  }

  // ---- events ----

  onViewChange(view: RuleView): void {
    this.view.set(view);
    this.search.set('');
    this.refreshResolutions();
  }

  onSearch(value: string): void {
    this.search.set(value);
  }

  onSelect(value: string): void {
    switch (this.view()) {
      case 'costCenter':
        this.selectedCostCenter.set(value);
        break;
      case 'concept':
        this.selectedConcept.set(value);
        break;
      default:
        this.selectedInventoryType.set(value);
    }
    this.refreshResolutions();
  }

  /** "New rule" pre-filled from what is selected in the current view. */
  openNew(): void {
    switch (this.view()) {
      case 'costCenter':
        this.openNewWith(GlAccountRuleScope.ExpenseClaim, GlAccountRuleRole.Expense, this.selectedCostCenter() || null, null, null);
        break;
      case 'concept':
        this.openNewWith(GlAccountRuleScope.ExpenseClaim, GlAccountRuleRole.Expense, null, this.selectedConcept() || null, null);
        break;
      default:
        this.openNewWith(
          GlAccountRuleScope.InventoryMovement,
          GlAccountRuleRole.Inventory,
          null,
          null,
          this.selectedInventoryType() || null,
        );
    }
  }

  openNewWith(
    scope: GlAccountRuleScope,
    role: GlAccountRuleRole,
    costCenterCode: string | null,
    conceptCode: string | null,
    inventoryTypeReference: string | null,
  ): void {
    if (!this.canEdit) return;
    this.drawer.set({ rule: null, defaults: { scope, role, costCenterCode, conceptCode, inventoryTypeReference } });
  }

  openEdit(rule: GlAccountRule): void {
    this.drawer.set({ rule, defaults: null });
  }

  onDrawerClosed(changed: boolean): void {
    this.drawer.set(null);
    if (changed) {
      this.loadRules();
    }
  }

  // ---- labels ----

  scopeLabel(scope: GlAccountRuleScope): string {
    return scope === GlAccountRuleScope.ExpenseClaim
      ? this.t('GlAccountRule_Scope_ExpenseClaim', 'Expense claim')
      : this.t('GlAccountRule_Scope_InventoryMovement', 'Inventory movement');
  }

  roleLabel(role: GlAccountRuleRole): string {
    switch (role) {
      case GlAccountRuleRole.Expense:
        return this.t('GlAccountRule_Role_Expense', 'Expense');
      case GlAccountRuleRole.Inventory:
        return this.t('GlAccountRule_Role_Inventory', 'Inventory');
      case GlAccountRuleRole.Offset:
        return this.t('GlAccountRule_Role_Offset', 'Offset');
      default:
        return this.t('GlAccountRule_Role_Tax', 'Tax');
    }
  }

  dimensionLabel(rule: GlAccountRule): string {
    if (rule.scope === GlAccountRuleScope.ExpenseClaim) {
      return _.isNil(rule.conceptCode) ? this.t('GlAccountRule_AnyConcept', 'Any concept') : this.conceptName(rule.conceptCode);
    }
    return _.isNil(rule.inventoryTypeReference)
      ? this.t('GlAccountRule_AnyInventoryType', 'Any inventory type')
      : this.inventoryTypeName(rule.inventoryTypeReference);
  }

  accountName(rule: GlAccountRule): string {
    return this.locale.getLocalizedValue(rule.glAccount.name) ?? '';
  }

  costCenterName(code: string): string {
    const center = this.costCenterByCode().get(code);
    return _.isNil(center) ? code : this.locale.getLocalizedValue(center.name) || code;
  }

  conceptName(code: string): string {
    const concept = this.concepts().find((c) => c.code === code);
    return _.isNil(concept) ? code : this.locale.getLocalizedValue(concept.name) || code;
  }

  inventoryTypeName(id: string): string {
    const type = this.inventoryTypes().find((t) => t.id === id);
    return _.isNil(type) ? id : this.locale.getLocalizedValue(type.name) || id;
  }

  conceptHeaderName(): string {
    return this.selectedConcept() ? this.conceptName(this.selectedConcept()) : '';
  }

  inventoryHeaderName(): string {
    return this.selectedInventoryType() ? this.inventoryTypeName(this.selectedInventoryType()) : '';
  }

  // ---- helpers ----

  /** The code, its parent, its parent's parent… up to the root; stops at a repeat so bad data can't loop. */
  private chain(code: string): string[] {
    const byCode = this.costCenterByCode();
    const chain: string[] = [];
    let current: string | null = code;
    while (!_.isNil(current) && !chain.includes(current)) {
      chain.push(current);
      current = byCode.get(current)?.parentCode ?? null;
    }
    return chain;
  }

  private hasChildren(code: string): boolean {
    return this.costCenters().some((c) => c.parentCode === code);
  }

  private inventoryWarnings(typeId: string): string[] {
    if (!typeId) return [];
    const rules = this.rules().filter((r) => r.scope === GlAccountRuleScope.InventoryMovement && r.inventoryTypeReference === typeId);
    const general = (role: GlAccountRuleRole) => rules.some((r) => r.role === role && _.isNil(r.costCenterCode));
    const warnings: string[] = [];
    if (!general(GlAccountRuleRole.Inventory)) {
      warnings.push(this.t('GlAccountRule_Warn_NoInventory', 'No general Inventory rule: receipts of this type cannot post.'));
    }
    if (!general(GlAccountRuleRole.Offset)) {
      warnings.push(this.t('GlAccountRule_Warn_NoOffset', 'No general Offset rule: a receipt would have no credit.'));
    }
    const expense = rules.filter((r) => r.role === GlAccountRuleRole.Expense);
    if (expense.length === 0) {
      warnings.push(this.t('GlAccountRule_Warn_NoExpense', 'No Expense rule: consumption cannot post.'));
    } else if (!expense.some((r) => _.isNil(r.costCenterCode))) {
      warnings.push(
        this.t(
          'GlAccountRule_Warn_ExpenseOnlyIn',
          'Expense only resolves in {list}; consumption in other cost centers will have no account.',
        ).replace(
          '{list}',
          expense
            .map((r) => r.costCenterCode)
            .filter((code): code is string => !_.isNil(code))
            .map((code) => this.costCenterName(code))
            .join(', '),
        ),
      );
    }
    return warnings;
  }

  private gapKey(kind: 'concept' | 'inventory', value: string): string {
    return `gap:${kind}:${value}`;
  }

  private coverageKey(costCenterCode: string): string {
    return `coverage:${costCenterCode}`;
  }

  private t(key: string, fallback: string): string {
    const value = this.translate.instant(key);
    return value === key ? fallback : value;
  }
}
